import { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Encounter } from "@medplum/fhirtypes";
import { CodingInput } from "@medplum/react";

import {
  Box,
  Button,
  Grid,
  Group,
  LoadingOverlay,
  NumberInput,
  Paper,
  Select,
  Stack,
  Title,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { UseFormReturnType } from "@mantine/form";

import { IconActivity } from "@tabler/icons-react";
import { toDate } from "date-fns";

import { TextWrapper } from "@/components/TextWrapper";
import { SignAndLockValidationErrorsContext } from "@/contexts/signAndLockValidationErrors";
import { DotPhraseRichTextField } from "@/dot-phrases";
import { VitalsHeartRateIcon } from "@/encounters/components/VitalSigns/components/VitalsFormLayout";
import { useEncounterGestationalAge } from "@/encounters/hooks/useEncounterGestationalAge";
import { useTypingAwareAutoSave } from "@/encounters/hooks/useTypingAwareAutoSave";
import {
  EncounterAnswersFormType,
  FetalPresentationChoices,
  FundalHeightUnitChoices,
} from "@/encounters/zod-schemas/encounter-answers";
import { QuiltedHealthValueSets } from "@/fhir/enums/quilted-health";
import { WeightUnitChoices } from "@/fhir/weight";
import { useIsBelowBreakpoint } from "@/hooks/useBreakpoint";
import { enumToSelectData } from "@/utils/obj";

import { PrenatalFlowsheetTable, usePrenatalFlowsheet } from "./PrenatalFlowsheetTable";

type PrenatalFlowsheetSectionProps = {
  form: UseFormReturnType<EncounterAnswersFormType>;
  editable: boolean;
  handleSubmit: (values: EncounterAnswersFormType) => Promise<void>;
  isAmendment?: boolean;
};

export default function PrenatalFlowsheetSection({
  form,
  editable,
  handleSubmit,
  isAmendment = false,
}: PrenatalFlowsheetSectionProps) {
  const isSmallScreen = useIsBelowBreakpoint("lg");
  const { clientId, encounterId } = useParams() as {
    clientId: string;
    encounterId: string;
  };

  const { encounter, observations, isLoading } = usePrenatalFlowsheet({
    clientId,
    encounterId,
  });

  const { signAndLockValidationErrors } = useContext(SignAndLockValidationErrorsContext);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const { gestationalAge, pregnancyRecord } = useEncounterGestationalAge(
    encounter ?? ({ resourceType: "Encounter" } as Encounter),
  );

  const effectiveEdd = pregnancyRecord?.edd;

  const performSave = useCallback(
    async (isAutoSave = false) => {
      setIsSaving(true);
      try {
        const { encounterFlowSheet } = form.values;
        let encounterFlowSheetDate = encounterFlowSheet?.encounterFlowSheetDate;

        const isEncounterFlowSheetEmpty = (data: typeof encounterFlowSheet): boolean => {
          if (!data) return true;
          return Object.values(data).every(
            (value) => value === undefined || value === "" || value === null,
          );
        };

        if (
          !isEncounterFlowSheetEmpty(encounterFlowSheet) &&
          !encounterFlowSheetDate &&
          encounter?.period?.start
        ) {
          encounterFlowSheetDate = toDate(encounter.period.start);
        }

        await handleSubmit({
          ...form.values,
          encounterFlowSheet: { ...form.values.encounterFlowSheet!, encounterFlowSheetDate },
        });

        // Only close form on manual save, not auto-save
        if (!isAutoSave) {
          setShowForm(false);
        }
      } finally {
        setIsSaving(false);
      }
    },
    [form, encounter, handleSubmit],
  );

  const onSave = form.onSubmit(async () => {
    await performSave(false);
  });

  // Auto-open flowsheet form when there are encounterFlowSheet errors (e.g. after Sign and Lock validation failure)
  useEffect(() => {
    const hasEncounterFlowSheetErrors = Object.keys(signAndLockValidationErrors).some((errorKey) =>
      errorKey.startsWith("encounterFlowSheet."),
    );
    if (hasEncounterFlowSheetErrors && !showForm) {
      setShowForm(true);
    }
  }, [signAndLockValidationErrors, showForm]);

  // Auto-populate date field with current date when form opens
  useEffect(() => {
    if (showForm && !form.values.encounterFlowSheet?.encounterFlowSheetDate) {
      form.setFieldValue("encounterFlowSheet.encounterFlowSheetDate", new Date());
    }
  }, [showForm, form]);

  // Auto-save functionality
  useTypingAwareAutoSave({
    enabled: showForm && editable && !isAmendment && form.isValid() && !isSaving,
    intervalMs: 60000,
    onAutoSave: () => performSave(true),
  });

  return (
    <Paper
      p={24}
      radius="lg"
      id="prenatal-flowsheet-section"
      style={{ backgroundColor: "white" }}
      data-testid="prenatal-flowsheet-section"
    >
      <Group pb="md" justify="space-between">
        <Title order={2} style={{ color: "var(--mantine-color-dark-4)" }}>
          <IconActivity size={24} style={{ marginRight: 8 }} />
          Prenatal Flow Sheet
        </Title>
      </Group>

      <Box pos="relative">
        <LoadingOverlay zIndex={90} visible={isLoading} overlayProps={{ radius: "sm", blur: 2 }} />
        <PrenatalFlowsheetTable
          isLoading={isLoading}
          eddDate={effectiveEdd}
          observations={observations}
          gestationalAge={gestationalAge}
          formValues={form.values.encounterFlowSheet}
        />
      </Box>

      <Group mt="md" justify="flex-end">
        {showForm ? (
          <>
            <Button
              c="burgundy.9"
              variant="outline"
              bd="1px solid burgundy.9"
              onClick={() => setShowForm(false)}
            >
              {isAmendment ? "Close" : "Cancel"}
            </Button>
            {!isAmendment && (
              <Button bg="burgundy.9" loading={isSaving} onClick={() => onSave()}>
                Save
              </Button>
            )}
          </>
        ) : (
          editable && (
            <Button
              variant="outline"
              color="burgundy.9"
              onClick={() => setShowForm(true)}
              data-testid="prenatal-flowsheet-add-values-button"
            >
              Add Values
            </Button>
          )
        )}
      </Group>

      {/*
          Gutter 0 in base allows for the masonry-like grid to be resposive.
          See the mb in the InputStack components for the spacing between the input groups.
          In lg, the gutter is set to "md" to provide horizontal spacing between the columns.
        */}
      {showForm && (
        <Stack gap="xs">
          <Grid justify="flex-start">
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <DateInput
                label="Date"
                data-testid="prenatal-flowsheet-date"
                {...form.getInputProps("encounterFlowSheet.encounterFlowSheetDate")}
              />
            </Grid.Col>
          </Grid>
          <Title mt="lg" order={3}>
            Weight
          </Title>
          <Grid justify="flex-start">
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <NumberInput
                allowNegative={false}
                label="Current Weight"
                data-testid="prenatal-flowsheet-weight"
                {...form.getInputProps("encounterFlowSheet.encounterFlowSheetWeight")}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <Select
                clearable
                label="Weight Unit"
                data={enumToSelectData(WeightUnitChoices)}
                data-testid="prenatal-flowsheet-weight-unit"
                {...form.getInputProps("encounterFlowSheet.encounterFlowSheetWeightUnit")}
              />
            </Grid.Col>
          </Grid>
          <Title mt="lg" order={3}>
            Urine section
          </Title>
          <Grid justify="flex-start">
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <CodingInput
                path=""
                creatable={false}
                clearable={false}
                name="urineProtein"
                withHelpText={false}
                label="Urine: Protein"
                binding={QuiltedHealthValueSets.UrineProtein}
                data-testid="prenatal-flowsheet-urine-protein"
                defaultValue={
                  form.values.encounterFlowSheet?.encounterFlowSheetUrineProtein?.code
                    ? form.values.encounterFlowSheet?.encounterFlowSheetUrineProtein
                    : undefined
                }
                {...form.getInputProps("encounterFlowSheet.encounterFlowSheetUrineProtein")}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <CodingInput
                path=""
                clearable={false}
                creatable={false}
                name="urineGlucose"
                withHelpText={false}
                label="Urine: Glucose"
                binding={QuiltedHealthValueSets.UrineGlucose}
                data-testid="prenatal-flowsheet-urine-glucose"
                defaultValue={
                  form.values.encounterFlowSheet?.encounterFlowSheetUrineGlucose?.code
                    ? form.values.encounterFlowSheet?.encounterFlowSheetUrineGlucose
                    : undefined
                }
                {...form.getInputProps("encounterFlowSheet.encounterFlowSheetUrineGlucose")}
              />
            </Grid.Col>
          </Grid>

          <Title mt="lg" order={3}>
            Blood Pressure and Pulse Reading
          </Title>

          <Grid justify="flex-start">
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <NumberInput
                label="Systolic BP"
                allowNegative={false}
                data-testid="prenatal-flowsheet-systolic-bp"
                {...form.getInputProps("encounterFlowSheet.encounterFlowSheetSystolicBP")}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <NumberInput
                label="Diastolic BP"
                allowNegative={false}
                data-testid="prenatal-flowsheet-diastolic-bp"
                {...form.getInputProps("encounterFlowSheet.encounterFlowSheetDiastolicBP")}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 2 }}>
              <NumberInput
                label="Pulse"
                allowNegative={false}
                data-testid="prenatal-flowsheet-pulse"
                leftSection={!isSmallScreen ? <VitalsHeartRateIcon /> : undefined}
                {...form.getInputProps("encounterFlowSheet.encounterFlowSheetHeartRate")}
              />
            </Grid.Col>
          </Grid>

          <Title mt="lg" order={3}>
            Edema
          </Title>

          <Grid justify="flex-start">
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <CodingInput
                path=""
                name="edema"
                label="Edema"
                clearable={false}
                creatable={false}
                withHelpText={false}
                binding={QuiltedHealthValueSets.Edema}
                data-testid="prenatal-flowsheet-edema"
                defaultValue={
                  form.values.encounterFlowSheet?.encounterFlowSheetEdema?.code
                    ? form.values.encounterFlowSheet?.encounterFlowSheetEdema
                    : undefined
                }
                {...form.getInputProps("encounterFlowSheet.encounterFlowSheetEdema")}
              />
            </Grid.Col>
          </Grid>

          <Title mt="lg" order={3}>
            Fundal Height
          </Title>

          <Grid justify="flex-start">
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <NumberInput
                label="Fundal Height"
                allowNegative={false}
                data-testid="prenatal-flowsheet-fundal-height"
                {...form.getInputProps("encounterFlowSheet.encounterFlowSheetFundalHeight")}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <Select
                clearable
                label="Fundal Height Detail"
                data={enumToSelectData(FundalHeightUnitChoices)}
                data-testid="prenatal-flowsheet-fundal-height-unit"
                {...form.getInputProps("encounterFlowSheet.encounterFlowSheetFundalHeightUnit")}
              />
            </Grid.Col>
          </Grid>

          <Title mt="lg" order={3}>
            Fetal
          </Title>

          <Grid justify="flex-start">
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <Select
                clearable
                label="Fetal Presentation"
                data={enumToSelectData(FetalPresentationChoices)}
                data-testid="prenatal-flowsheet-fetal-presentation"
                {...form.getInputProps("encounterFlowSheet.encounterFlowSheetFetalPresentation")}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 2 }}>
              <NumberInput
                allowNegative={false}
                label="Fetal Heart Rate"
                data-testid="prenatal-flowsheet-fhr"
                leftSection={!isSmallScreen ? <VitalsHeartRateIcon /> : undefined}
                {...form.getInputProps("encounterFlowSheet.encounterFlowSheetFetalHeartRate")}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <CodingInput
                path=""
                creatable={false}
                clearable={false}
                name="fetalMovement"
                withHelpText={false}
                label="Fetal Movement"
                binding={QuiltedHealthValueSets.FetalMovement}
                data-testid="prenatal-flowsheet-fetal-movement"
                defaultValue={
                  form.values.encounterFlowSheet?.encounterFlowSheetFetalMovement?.code
                    ? form.values.encounterFlowSheet?.encounterFlowSheetFetalMovement
                    : undefined
                }
                {...form.getInputProps("encounterFlowSheet.encounterFlowSheetFetalMovement")}
              />
            </Grid.Col>
          </Grid>

          <TextWrapper size="sm">Notes</TextWrapper>
          <DotPhraseRichTextField
            data-testid="prenatal-flowsheet-notes-editor"
            {...form.getInputProps("encounterFlowSheet.encounterFlowSheetNotes")}
          />

          <Group mt="xl" justify="flex-end">
            <Button
              c="burgundy.9"
              variant="outline"
              bd="1px solid burgundy.9"
              onClick={() => setShowForm(false)}
            >
              {isAmendment ? "Close" : "Cancel"}
            </Button>
            {!isAmendment && (
              <Button
                bg="burgundy.9"
                loading={isSaving}
                onClick={() => onSave()}
                data-testid="prenatal-flowsheet-save-button"
              >
                Save
              </Button>
            )}
          </Group>
        </Stack>
      )}
    </Paper>
  );
}
